export enum Page {
  Login = 'login',
  ForgotPassword = 'forgot-password',
  CreateUser = 'create-user',
  UserManagement = 'user-management',
  CustomerLookup = 'customer-lookup',
  LeadLookup = 'lead-lookup',
  DiscountsPage = 'discounts',
  ManagePPSSurvey = 'post-checkout-survey-options',
  ManageDataPage = 'manage-data',
  ItemsPage = 'items',
  ProductItemsPage = 'product-items',
  PromotionsPage = 'promotions',
  DiscountLookup = 'discount-lookup',
  ProductPricingNutrientInfo = 'product-pricing-nutrient-info',
  FetchAiPage = 'fetch-ai',
  OnCallDashboard = 'on-call-dashboard',
  CommandRunner = 'command-runner',
}

export type Group = {
  id: string;
  name: string;
};

export enum AccountStatus {
  Active = 'ACTIVE',
  Pending = 'PENDING',
  Cancelled = 'CANCELLED',
}

export enum UserGroup {
  Admin = 'BarkAdmin',
  TeamMember = 'BarkTeamMember',
  OpsAdmin = 'BarkOpsAdmin',
  MarketingAdmin = 'BarkMarketingAdmin',
  AllModuleAccess = 'BarkAllModuleAccess',
}

export enum PromotionSurface {
  FUNNEL = 'FUNNEL',
  PORTAL = 'PORTAL',
  REACTIVATION = 'REACTIVATION',
}

export type BarkUser = {
  first_name: string;
  last_name: string;
  email: string;
  groups: Group[];
  is_active: string;
  created: string;
  last_updated: string;
};

export enum PetStatus {
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  CANCELLED = 'CANCELLED',
  DECEASED = 'DECEASED',
  DECEASED_TO_NOT_LIST = 'DECEASED_TO_NOT_LIST',
  UNKNOWN = 'UNKNOWN',
}

export enum PetPlanStatus {
  ACTIVE = 'ACTIVE',
  HOLD = 'HOLD',
  CANCELLED = 'CANCELLED',
  PAUSED = 'PAUSED',
  INITIALIZING = 'INITIALIZING',
}

export type BasePetProperty = {
  name: string;
  slug: string;
};

export type PetOptions = {
  breeds: BasePetProperty[];
  lifestyles: BasePetProperty[];
  body_types: BasePetProperty[];
  health_conditions: BasePetProperty[];
};

export type PetProperties = {
  id: string;
  created: string;
  last_updated: string;
  pet: string;
  recommended_calories_day: string | null;
  weight: number;
  neutered: boolean;
  body_type: string;
  lifestyle: string;
  health_conditions: string[];
};

export type Pet = {
  id: string;
  name: string;
  status: PetStatus;
  gender: string;
  birth_month?: string;
  birth_year?: string;
  breeds?: string[];
  properties?: PetProperties;
  pet_plan?: PetPlan;
  account?: string;
};
export type PetPlan = {
  account: string;
  id: string;
  calories_per_day: number;
  discount_amt: number;
  discounted_total_price: number;
  frequency: number;
  is_topper: boolean;
  last_updated: string;
  next_charge_date: string;
  pet: string;
  plan_size: number;
  products: PetPlanProduct[];
  recipe_data: Recipe[];
  recipes: string[];
  start_date: string;
  status: string;
  status_date: string;
  total_price: number;
  flex_frequency_selection: number | null;
};

export type PetPlanProduct = {
  id: string;
  code: string;
  created: string;
  frequency: number;
  name: string;
  description_bark: string;
  next_charge_date: string;
  price_per_week: string;
  product_id: string;
  product_type: string;
  quantity: number;
  recipes: Recipe[];
  status: string;
  type: string;
  unit_price: string;
};

export type FulfillmentProduct = {
  id: string;
  pet_id: string;
  pet_plan_id: string;
  plan_size: PlanSize;
  code: string;
  name: string;
  description_bark: string;
  discount_amount: number;
  historical_product_sku: string;
  is_frozen: boolean;
  items: Item[];
  price: number;
  quantity: number;
  refund_amount: string | null;
  tax_amount: number;
  type: ProductType;
  has_claim: boolean;
};

export type PlanSize = { id: number; scoop_calories: number };

export type Order = {
  id: string;
  account: string;
  products: OrderProduct[];
  total_price: string | null;
  total_refunds: string;
  discounts: OrderDiscount[];
  charged: string | null;
  scheduled?: string;
  status: OrderStatus;
  order_type: OrderType;
  pricing_model: PricingModel | null;
  fulfillments: Fulfillment[];
  payment_processor: string;
  shipping_method: string | null;
  total_shipping_price: string | null;
  total_shipping_refund: string | null;
  shipping_tax: string | null;
  shipping_subtotal_price: string | null;
  min_delivery_date: string | null;
  max_delivery_date: string | null;
  is_frozen: boolean | null;
  currency: string;
  is_modified: boolean | null;
};

export type OrderProductPetPlan = {
  id: string;
  pet_id: string;
  pet_name: string;
  plan_size_code: string;
  recipes: string[];
  start_date: string;
  status: PetPlanStatus;
};

export type OrderProduct = {
  id: string;
  code: string;
  name: string;
  pet_plan?: OrderProductPetPlan; // Note: pet_plan may not exist on older migrated orders.
  product: string;
  product_type: ProductType;
  quantity: number;
  recipes: Recipe[];
  type: OrderProductType;
  unit_price: string;
  tax_amount: string;
  discount_amount: string;
  refund_amount: string | null;
  has_promotion: boolean;
  price: Price;
  image_uri: string | null;
  is_frozen: boolean | null;
};

export enum OrderStatus {
  PREVIEW = 'PREVIEW',
  PROCESSING = 'PROCESSING',
  CHARGING = 'CHARGING',
  MAX_RETRY_REACHED = 'MAX_RETRY_REACHED',
  READY_FOR_FULFILLMENT = 'READY_FOR_FULFILLMENT',
  FULFILLING = 'FULFILLING',
  PARTIALLY_FULFILLED = 'PARTIALLY_FULFILLED',
  FULFILLED = 'FULFILLED',
  COMPLETE = 'COMPLETE',
  HOLD = 'HOLD',
  CANCELLED = 'CANCELLED',
  ERROR = 'ERROR',
  HISTORICAL = 'HISTORICAL',
  RUSH_ORDER = 'RUSH_ORDER',
}

export enum OrderType {
  TRIAL = 'TRIAL',
  SUBSCRIPTION = 'SUBSCRIPTION',
  ONETIME = 'ONETIME',
  REPLACEMENT = 'REPLACEMENT',
  MODIFICATION = 'MODIFICATION',
}

export enum OrderProductType {
  SUBSCRIPTION = 'SUBSCRIPTION',
  ADDON = 'ADDON',
  ONETIME = 'ONETIME',
  REPLACEMENT = 'REPLACEMENT',
  MODIFICATION = 'MODIFICATION',
}

export type Orders = {
  past: Order[];
  processing: Order[];
  upcoming: Order[];
};

export enum ClaimTypes {
  ADVERSE_REACTION = 'Adverse Reaction',
  FEEDING_GUIDE = 'Feeding Guide',
  MISPACK = 'Mispack',
  ORDER_MANIFEST = 'Order Manifest',
  PRODUCT_QUALITY = 'Product Quality',
  ROOF = 'Run Out Of Food',
  WARM_BOX = 'Warm Box',
}

export type Fulfillment = {
  has_claim: boolean;
  address1: string;
  address2: string | null;
  box_number: number;
  city: string;
  company: string | null;
  country: CountryCode;
  created: string;
  first_name: string;
  fulfillment_center: string;
  id: string;
  last_name: string;
  last_updated: string;
  order: string;
  orderdesk_id: string;
  pet: string;
  pet_ids: string[];
  shipments: Shipment[];
  shopify_order_id: null;
  state: string;
  status: FulfillmentStatus;
  zip: string;
  products: FulfillmentProduct[];
  is_split_fulfillment: boolean;
  carrier_rate_class: string | null;
  has_shared_claim: boolean;
  has_damaged_box_claim: boolean;
};

export enum FulfillmentStatus {
  SCHEDULED = 'SCHEDULED',
  FULFILLMENTS_CREATED = 'FULFILLMENTS_CREATED',
  PREPARING = 'PREPARING',
  ORDERDESK_SENT = 'ORDERDESK_SENT',
  CREATING_AFTERSHIP_SHIPMENTS = 'CREATING_AFTERSHIP_SHIPMENTS',
  PARTIALLY_SHIPPED = 'PARTIALLY_SHIPPED',
  SHIPPED = 'SHIPPED',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',
  ERROR = 'ERROR',
  DELIVERY_FAILED = 'DELIVERY_FAILED',
}

export type Shipment = {
  aftership_exception_message: string | null;
  aftership_object: AftershipObject;
  aftership_tracking_id: string | null;
  carrier_code: CarrierCode | string | null;
  cost_of_shipping: string;
  created: string;
  delivered: string | null;
  exception: string | null;
  fulfillment: string;
  id: string;
  in_transit: string | null;
  last_updated: string;
  orderdesk_id: string | null;
  out_for_delivery: string | null;
  available_for_pickup: string | null;
  returned: string | null;
  shipped: string | null;
  shipping_class: string | null;
  tracking_number: string | null;
  voided: string | null;
};

type AftershipObject = {
  id: string;
  last_updated_at: string;
  tag: string;
};

export enum CarrierCode {
  FEDEX = 'fedex',
  UPS = 'ups',
}
export enum ProductType {
  RECIPE = 'RECIPE',
  MEAL = 'MEAL',
  SCOOP = 'SCOOP',
  SNACK = 'SNACK',
  MATERIAL = 'MATERIAL',
  REPLACE = 'REPLACE',
  SAMPLE = 'SAMPLE',
  SUPPLEMENT = 'SUPPLEMENT',
  MERCH = 'MERCH',
  BUFFER = 'BUFFER',
  PUPGUM = 'PUPGUM',
  BUNDLE = 'BUNDLE',
}

export type ProductCodesResponse = {
  id: string;
  code: string;
  type: ProductType;
  is_frozen: boolean;
};

export enum CountryCode {
  US = 'US',
  CA = 'CA',
}

export enum PostCheckoutSurveyOptionStatus {
  ACTIVE = 'ACTIVE',
  HIDDEN = 'HIDDEN',
}

export type Customer = {
  id: string;
  addresses: Address[];
  first_name: string;
  last_name: string;
  email: string;
  pets: Pet[];
  created: string;
  last_updated: string;
  phone_number: string;
  gladly_customer_id: string | null;
  orders: Orders;
  status?: string;
  petplans: PetPlan[];
  survey_responses: any[]; // TODO: Define this as a Bark Type
  default_shipping_method_id: string;
  default_pricing_model: PricingModel | null;
  proactive_customer_support_opted_in: boolean;
  primary_payment_processor: string;
  default_country: CountryCode;
  default_currency: string;
  contact_preference_order: string;
  first_trial_pet_flex_frequency_selection: number | null;
  has_six_week_frequency: boolean | null;
};

export type Lead = {
  id: string;
  account_id: string | null;
  email: string;
  country: CountryCode;
  account_gladly_customer_id: string | null;
  account_status?: string | null;
  pricing_model: PricingModel | null;
  funnel_session_pet_answers: LeadPetAnswer[];
};

export type LeadPetAnswer = {
  id: string;
  pet_index: number;
  name: string | null;
  plan_size: string | null;
};

export type Address = {
  id: string;
  account: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string;
  zip: string;
  country: CountryCode;
  latitude: string | null;
  longitude: string | null;
  is_primary: boolean;
  type: 'SHIPPING' | 'BILLING';
  shipping_name: string | null;
};

export type Option = {
  value: string;
  label: string;
  hasBadge?: boolean; //Can be used if needed to show badge
  isDisabled?: boolean; //Can be used if needed to disable the option
};

export type StatusOption = Option & { className: string };

export type ProductCodes = {
  id: string;
  code: string;
  type: string;
};

export enum ChangelogOperation {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export type CustomerChangelogItem = {
  id: string;
  application: string | null;
  accountId: string;
  table: string;
  fieldName: string;
  newValue: string;
  petId: string | null;
  operationType: ChangelogOperation;
  authorEmail: string;
  rowPrimaryKey: string;
  createdAt: string;
};

export type SystemChangelogItem = {
  category: string;
  objectId: string;
  application: string | null;
  createdAt: string;
  fieldName: string;
  newValue: string;
  operationType: ChangelogOperation;
  authorEmail: string;
};

export type ChangelogItem = CustomerChangelogItem | SystemChangelogItem;

export type OrderDiscount = {
  id: string;
  discount: Discount;
  amount: string;
  created: string;
  order: string;
  status: string;
};

export type Discount = {
  value: string;
  order_onetime: boolean;
  order_trial: boolean;
  order_subscription: boolean;
  reactivation_only: boolean;
  name: string;
  limit_type: string;
  limit_value: string;
  discount_type: string;
  available_end_date: string;
  available_start_date: string;
  code: string;
  id: string;
  is_active: boolean;
  created: string;
  eligible_country: string;
};

export enum DiscountExclusiveType {
  WINBACK_10_PERCENT = 'WINBACK_10_PERCENT',
  WINBACK_30_PERCENT = 'WINBACK_30_PERCENT',
  CX_10_PERCENT = 'CX_10_PERCENT',
  CX_10_PERCENT_FOR_THREE_ORDERS = 'CX_10_PERCENT_FOR_THREE_ORDERS',
  CX_30_PERCENT = 'CX_30_PERCENT',
  CX_REPROCESS_100_PERCENT = 'CX_REPROCESS_100_PERCENT',
  CX_CUSTOM_CODE = 'CX_CUSTOM_CODE',
  CX_50_PERCENT_MEAL_PLAN_PRODUCTS = 'CX_50_PERCENT_MEAL_PLAN_PRODUCTS',
}

export enum DiscountCreationSuccessMessage {
  CX_10_PERCENT = '10% off 1 order discount code applied to order',
  CX_10_PERCENT_FOR_THREE_ORDERS = '10% off 3 orders discount code applied to customer account',
  CX_30_PERCENT = '30% off 1 order discount code applied to order',
  CX_50_PERCENT_MEAL_PLAN_PRODUCTS = '50% off 1 order (meal plan products only) discount code applied to order',
}

export type Treatment = {
  feature_flag: string;
  feature_flag_status: string;
  treatment_distribution: number;
  treatment_control: boolean;
  treatment_name: string;
  created: string;
};

export type Item = {
  id: string;
  sku: string;
  name: string;
  quantity: number;
  recipe: string;
  weight_oz: number;
  volume: number;
};

export enum RecipeType {
  UNKIBBLE = 'UNKIBBLE',
  FRESH = 'FRESH',
  SNACK = 'SNACK',
  SCOOP = 'SCOOP',
  MATERIAL = 'MATERIAL',
  SUPPLEMENT = 'SUPPLEMENT',
  MERCH = 'MERCH',
  DC = 'DC',
  AIRDRIED = 'AIRDRIED',
}

export type Recipe = {
  id: string;
  description: string;
  name: string;
  seq_no: number;
  aafco_statement: string;
  type: RecipeType;
};

export type BuildPlanPayload = {
  discounts: string[];
  plan_size: string;
  recipes: string[];
  account_id: string;
  frequency?: number;
};

export type ProductRelatedItem = {
  sku: string;
  quantity: number;
  id?: string; // This is only used in the UI, unique key for the list
  country?: CountryCode;
};

export type ProductItem = {
  id: string;
  code: string;
  name: string;
  status_us: string;
  status_ca: string;
  items: ProductRelatedItem[];
};

export type PricingModel = {
  created: string;
  creator: string | null;
  description?: string;
  id: string;
  is_default: boolean;
  last_updated: string;
  name: string;
  status: string;
};

export enum PromotionType {
  ORDER = 'ORDER',
  PET = 'PET',
}

export enum PromotionStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export type Promotion = {
  id: string;
  name: string;
  promotion_type: PromotionType;
  promotion_surface: PromotionSurface;
  banner_copy: string;
  marketing_site_desktop_banner_copy: string;
  marketing_site_mobile_banner_copy: string;
  created_by: string;
  free_product_code: string;
  paid_product_code: string | null;
  paid_quantity: number | null;
  auto_renew: boolean | null;
  disclaimer: string | null;
  quantity: number;
  minimum_order_gross_value_threshold_amount: number;
  start_timestamp: string;
  end_timestamp: string;
  status: PromotionStatus;
  required_conditions_products: string[];
  created: string;
  last_updated: string;
  currency: string;
  eligible_country: CountryCode;
  subscription_order_number?: number | string | null;
  toast_title_copy?: string | null;
  toast_body_copy?: string | null;
  funnel_checkout_display_name?: string | null;
};

export type Price = {
  id: string;
  price: string;
  price_per_meal: string | null;
  price_per_day: string | null;
  price_per_week: string | null;
  discounted_price_per_pack: string | null;
  discounted_price_per_meal: string | null;
  discounted_price_per_day: string | null;
  discounted_price_per_week: string | null;
  discounted_price: string | null;
  pricing_model: PricingModel;
  created: string;
  last_updated: string;
  frequency: number;
};

export enum GetProductsRequestType {
  ONE_TIME_ORDER = 'one-time-order',
  ONE_TIME_PRODUCT = 'one-time-product',
  ONE_TIME_ORDER_PRODUCTS = 'one-time-order-non-recurring',
}

export enum OneTimeModalType {
  ONE_TIME_ORDER = 'one-time-order',
  ONE_TIME_PRODUCT = 'one-time-product',
}

export interface OrderFulfillmentProduct extends Omit<FulfillmentProduct, 'discount_amount'> {
  isMealProduct: boolean;
  tax_amount: number;
  fulfillment: Fulfillment;
  order: Order;
  unit_price: string;
  discount_amount: string;
  refund_amount: string | null;
  has_claim: boolean;
}

export interface BufferBag {
  pet_id: string;
  pet_name: string;
  pet_plan_id: string;
  buffer_bag_extra_days: number;
  buffer_bag_number_of_bags_allowed: number;
  buffer_bag_products: BufferBagProduct[];
}

export interface BufferBagProduct {
  recipe_id: string;
  recipe_name: string;
  buffer_bag_product_code: string;
  buffer_bag_price: string;
}

export interface TangoTeamPriceModel {
  currency: string;
  price: number;
  price_per_day: number;
  price_per_week: number;
  pricing_model_id: string;
  pricing_model_name: string;
}

export interface TangoTeamItem {
  quantity: number;
  sku: string;
  weight_oz: number;
  volume: number;
  recipe_id: string;
  country?: CountryCode;
}

export interface TangoTeamPlanSize {
  id: number;
  created: string;
  last_updated: string;
  calories_per_day: number;
  scoops_per_day: number;
  scoop_calories: number;
  scoop_code: string;
  portioning_information: any;
}

export interface TangoTeamProduct {
  code: string;
  description_bark: string;
  type: string;
  recurring: boolean;
  frequency: number;
  plan_size_id: string;
  status_us: string;
  status_ca: string;
  item_info: TangoTeamItem[];
  plan_size: TangoTeamPlanSize;
  recipe_data_point?: string;
  prices: Record<string, TangoTeamPriceModel>;
  scoop_codes: string[];
}

export interface NutrientAnalysisInfo {
  recipe_id: string;
  nutrient_category: string;
  name: string;
  as_fed_basis: string;
  dry_matter_basis: string;
  seq_no: string;
}

export type DuplicateDiscountPayload = {
  discount_type?: string;
  value?: number | null;
  trial_value?: number | null;
  max_redemption_amount?: number | null;
  description?: string;
  limit_type?: string;
  limit_value?: number | null;
  available_start_date?: string | null;
  available_end_date?: string | null;
  is_active?: boolean;
  order_trial?: boolean;
  order_subscription?: boolean;
  order_onetime?: boolean;
  reactivation_only?: boolean;
  eligible_products?: string[];
  exempt_from_sale_state?: boolean;
  currency?: string;
  eligible_country?: string;
};

export type PostCheckoutSurveyOption = {
  id: string;
  label: string;
  value: string;
  status: PostCheckoutSurveyOptionStatus;
  country: CountryCode;
  marketing_source: string | null;
  marketing_bucket: string | null;
};
